/**
 * Collections Worklist Page
 * Displays prioritized list of delinquent accounts with recommended actions.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatCurrency,
  formatDate,
  getCollectionWorklist,
  getDelinquencyBucketColor,
  getDelinquencyBucketLabel,
  logCollectionAction,
  type WorklistItem,
} from '../../services/bhphApi';

type DelinquencyBucket = 'all' | 'early' | 'moderate' | 'serious' | 'critical' | 'severe';

const CollectionsWorklistPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedBucket, setSelectedBucket] = useState<DelinquencyBucket>('all');
  const [selectedAccount, setSelectedAccount] = useState<WorklistItem | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [sortBy, setSortBy] = useState<'priority' | 'days_past_due' | 'balance'>('priority');

  // Fetch worklist
  const {
    data: worklistData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['collectionWorklist', selectedBucket],
    queryFn: () => getCollectionWorklist(selectedBucket === 'all' ? undefined : selectedBucket),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Log action mutation
  const logActionMutation = useMutation({
    mutationFn: ({ accountId, data }: { accountId: string; data: any }) =>
      logCollectionAction(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collectionWorklist'] });
      setShowActionModal(false);
      setSelectedAccount(null);
    },
  });

  // Sort worklist
  const sortedWorklist = React.useMemo(() => {
    if (!worklistData?.worklist) return [];

    const sorted = [...worklistData.worklist];

    switch (sortBy) {
      case 'priority':
        return sorted.sort((a, b) => b.priority_score - a.priority_score);
      case 'days_past_due':
        return sorted.sort((a, b) => b.days_past_due - a.days_past_due);
      case 'balance':
        return sorted.sort((a, b) => b.total_balance - a.total_balance);
      default:
        return sorted;
    }
  }, [worklistData, sortBy]);

  const handleQuickAction = (account: WorklistItem, _actionType: string) => {
    setSelectedAccount(account);
    setShowActionModal(true);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Collection Worklist</h1>
          <p className="text-gray-600 mt-1">
            {worklistData?.total_accounts || 0} accounts requiring attention
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded transition duration-150"
          >
            🔄 Refresh
          </button>
          <button
            onClick={() => navigate('/bhph/dashboard')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition duration-150"
          >
            ← Dashboard
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Delinquency Bucket Filter */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Delinquency:
            </label>
            <div className="flex flex-wrap gap-2">
              <FilterButton
                label="All"
                active={selectedBucket === 'all'}
                onClick={() => setSelectedBucket('all')}
                color="gray"
              />
              <FilterButton
                label="Early"
                active={selectedBucket === 'early'}
                onClick={() => setSelectedBucket('early')}
                color="yellow"
              />
              <FilterButton
                label="Moderate"
                active={selectedBucket === 'moderate'}
                onClick={() => setSelectedBucket('moderate')}
                color="orange"
              />
              <FilterButton
                label="Serious"
                active={selectedBucket === 'serious'}
                onClick={() => setSelectedBucket('serious')}
                color="red"
              />
              <FilterButton
                label="Critical"
                active={selectedBucket === 'critical'}
                onClick={() => setSelectedBucket('critical')}
                color="purple"
              />
              <FilterButton
                label="Severe"
                active={selectedBucket === 'severe'}
                onClick={() => setSelectedBucket('severe')}
                color="red"
              />
            </div>
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="priority">Priority Score</option>
              <option value="days_past_due">Days Past Due</option>
              <option value="balance">Balance Amount</option>
            </select>
          </div>
        </div>
      </div>

      {/* Worklist Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Loading worklist...</p>
        </div>
      ) : sortedWorklist.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Accounts to Display</h2>
          <p className="text-gray-600">
            {selectedBucket === 'all'
              ? 'Great! No delinquent accounts at this time.'
              : `No accounts in the "${selectedBucket}" bucket.`}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recommended Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedWorklist.map((account) => (
                  <tr
                    key={account.account_id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/bhph/account/${account.account_id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <PriorityScore score={account.priority_score} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {account.customer_name}
                      </div>
                      <div className="text-sm text-gray-500">{account.account_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.vehicle}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <DelinquencyBadge
                        bucket={account.delinquency_bucket}
                        daysPastDue={account.days_past_due}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(account.total_balance)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Payment: {formatCurrency(account.payment_amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {account.last_action_type ? (
                        <>
                          <div className="font-medium">{account.last_action_type}</div>
                          <div className="text-xs">{formatDate(account.last_action_date)}</div>
                        </>
                      ) : (
                        'No actions yet'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <RecommendedActionBadge action={account.recommended_action} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickAction(account, 'CALL');
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        📞 Call
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/bhph/payment?account=${account.account_id}`);
                        }}
                        className="text-green-600 hover:text-green-900"
                      >
                        💵 Pay
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Action Modal */}
      {showActionModal && selectedAccount && (
        <QuickActionModal
          account={selectedAccount}
          onClose={() => {
            setShowActionModal(false);
            setSelectedAccount(null);
          }}
          onSubmit={(data) => {
            logActionMutation.mutate({
              accountId: selectedAccount.account_id,
              data,
            });
          }}
        />
      )}
    </div>
  );
};

// ===== Component: Filter Button =====
interface FilterButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  color: string;
}

const FilterButton: React.FC<FilterButtonProps> = ({ label, active, onClick, color }) => {
  const colorClasses: Record<string, string> = {
    gray: active ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
    yellow: active
      ? 'bg-yellow-600 text-white'
      : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
    orange: active
      ? 'bg-orange-600 text-white'
      : 'bg-orange-100 text-orange-800 hover:bg-orange-200',
    red: active ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800 hover:bg-red-200',
    purple: active
      ? 'bg-purple-600 text-white'
      : 'bg-purple-100 text-purple-800 hover:bg-purple-200',
  };

  return (
    <button
      onClick={onClick}
      className={`${colorClasses[color]} px-3 py-1 rounded font-medium text-sm transition duration-150`}
    >
      {label}
    </button>
  );
};

// ===== Component: Priority Score =====
const PriorityScore: React.FC<{ score: number }> = ({ score }) => {
  const getColor = (score: number) => {
    if (score >= 70) return 'bg-red-100 text-red-800 border-red-300';
    if (score >= 50) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (score >= 30) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-green-100 text-green-800 border-green-300';
  };

  return (
    <div className={`inline-flex items-center px-3 py-1 rounded-full border-2 ${getColor(score)}`}>
      <span className="text-xl font-bold">{score}</span>
    </div>
  );
};

// ===== Component: Delinquency Badge =====
const DelinquencyBadge: React.FC<{ bucket: string; daysPastDue: number }> = ({
  bucket,
  daysPastDue,
}) => {
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    orange: 'bg-orange-100 text-orange-800',
    red: 'bg-red-100 text-red-800',
    purple: 'bg-purple-100 text-purple-800',
  };

  const color = getDelinquencyBucketColor(bucket);
  const label = getDelinquencyBucketLabel(bucket);

  return (
    <div>
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses[color]}`}
      >
        {label}
      </span>
      <div className="text-xs text-gray-600 mt-1">{daysPastDue} days past due</div>
    </div>
  );
};

// ===== Component: Recommended Action Badge =====
const RecommendedActionBadge: React.FC<{ action: string }> = ({ action }) => {
  const actionLabels: Record<string, string> = {
    SEND_FRIENDLY_REMINDER_SMS: '📱 Send SMS',
    CALL_CUSTOMER: '📞 Call Now',
    RETRY_CALL: '📞 Retry Call',
    FOLLOW_UP_CALL: '📞 Follow Up',
    CALL_AND_ARRANGE_PAYMENT_PLAN: '💬 Payment Plan',
    FOLLOW_UP_ON_BROKEN_PROMISE: '⚠️ Broken Promise',
    WAIT_FOR_PROMISE_PAYMENT: '⏳ Wait for Payment',
    SCHEDULED_FOLLOW_UP: '📅 Scheduled',
    ESCALATE_TO_MANAGER: '🚨 Escalate',
    CONTINUE_MONITORING: '👀 Monitor',
  };

  return (
    <span className="text-sm font-medium text-blue-600">{actionLabels[action] || action}</span>
  );
};

// ===== Component: Quick Action Modal =====
const QuickActionModal: React.FC<{
  account: WorklistItem;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ account, onClose, onSubmit }) => {
  const [actionType, setActionType] = useState('CALL');
  const [contactOutcome, setContactOutcome] = useState('CONTACTED');
  const [notes, setNotes] = useState('');
  const [promiseAmount, setPromiseAmount] = useState('');
  const [promiseDate, setPromiseDate] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      action_type: actionType,
      contact_method: actionType === 'CALL' ? 'PHONE' : actionType === 'SMS' ? 'SMS' : 'EMAIL',
      contact_outcome: contactOutcome,
      notes,
      promise_amount: promiseAmount ? parseFloat(promiseAmount) : undefined,
      promise_date: promiseDate || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Log Collection Action</h2>
        <div className="text-sm text-gray-600 mb-4">
          <strong>{account.customer_name}</strong> - {account.vehicle}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
            <select
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="CALL">Phone Call</option>
              <option value="SMS">SMS Message</option>
              <option value="EMAIL">Email</option>
              <option value="NOTE">Note</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Outcome</label>
            <select
              value={contactOutcome}
              onChange={(e) => setContactOutcome(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="CONTACTED">Contacted</option>
              <option value="NO_ANSWER">No Answer</option>
              <option value="LEFT_MESSAGE">Left Message</option>
              <option value="PROMISE">Made Promise</option>
              <option value="REFUSED">Refused</option>
            </select>
          </div>

          {contactOutcome === 'PROMISE' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Promise Amount
                </label>
                <input
                  type="number"
                  value={promiseAmount}
                  onChange={(e) => setPromiseAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="450.00"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Promise Date</label>
                <input
                  type="date"
                  value={promiseDate}
                  onChange={(e) => setPromiseDate(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
              rows={3}
              placeholder="Add notes about this interaction..."
            />
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition duration-150"
            >
              Save Action
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded transition duration-150"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CollectionsWorklistPage;
